import React from "react";
import { connect } from "react-redux";

import List from "../core/components/list/List";
import ListItem from "../core/components/list/ListItem";
import { Alignment, Side, Size } from "../core/types/system/Layout";
import GeneralContainer from "../core/components/containers/GeneralContainer";
import MainToolbarContents from "../core/components/toolbar/MainToolbarContents";
import ButtonMenu from "../core/components/buttons/ButtonMenu";
import Button from "../core/components/buttons/Button";
import { addAlert } from "../core/state/system/Alerts";
import { AlertTypes } from "../core/types/system/AlertEntry";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import {
    deleteOrganisers,
    initialisePublishedOrganiserDetails,
} from "../core/state/organiser/Organisers";
import PublishedOrganiser from "../core/types/organiser/PublishedOrganiser";
import Collapsible from "../core/components/collapsible/Collapsible";
import ContextFragment from "../core/components/contextmenu/ContextFragment";
import ContextMenuItem from "../core/components/contextmenu/ContextMenuItem";
import QueuedExportFile from "../core/types/system/QueuedExportFile";
import FileTypes from "../core/types/enums/FileTypes";
import QueuedExportSubTypes from "../core/types/enums/QueuedExportSubType";
import { addExports } from "../core/state/administration/Exports";
import User from "../core/types/core/User";
import ToolbarButton from "../core/components/toolbar/ToolbarButton";
import { openRecycled, RecycledType } from "../core/state/system/Recycled";
import Recycled from "../core/components/recycled/Recycled";
import Text from "../core/components/text/Text";
import { IAppState } from "../core/state/Store";

import "./Organisers.scss";

interface OrganisersProps {
    user: User;
    organisers: PublishedOrganiser[];

    setPageLoading: (loading: boolean) => void;
    initialisePublishedOrganiserDetails: () => Promise<void>;
    deleteOrganisers: (organisers: PublishedOrganiser[]) => Promise<void>;
    addExports: (exports: QueuedExportFile[]) => Promise<void>;
    openRecycled: (types: RecycledType[]) => void;
}

interface OrganisersState {
    groupsOpen: Map<string, boolean>;
    organisers: {
        groupName: string;
        open: boolean;
        organisers: PublishedOrganiser[];
    }[];
    selections: PublishedOrganiser[];
}

class Organisers extends React.Component<OrganisersProps, OrganisersState> {
    state: OrganisersState = {
        groupsOpen: new Map(),
        organisers: [],
        selections: [],
    };

    async componentDidMount() {
        try {
            this.props.setPageLoading(true);
            await this.props.initialisePublishedOrganiserDetails();
            this.buildState();
        } finally {
            this.props.setPageLoading(false);
        }
    }

    componentDidUpdate(prevProps: OrganisersProps, prevState: OrganisersState) {
        if (
            prevProps.organisers !== this.props.organisers ||
            prevState.groupsOpen !== this.state.groupsOpen
        )
            this.buildState();
    }

    buildState() {
        this.setState(prevState => ({
            groupsOpen: prevState.groupsOpen,
            organisers: Array.from(
                this.props.organisers.groupBy(item => item.groupName),
            ).map(group => ({
                groupName: group[0],
                open: prevState.groupsOpen.get(group[0]) ?? true,
                organisers: group[1],
            })),
            selections: prevState.selections,
        }));
    }

    toggleOpen(groupName: string, open: boolean) {
        this.setState(prevState => {
            const newGroupsOpen = new Map(prevState.groupsOpen);
            newGroupsOpen.set(groupName, open);
            return {
                groupsOpen: newGroupsOpen,
            };
        });
    }

    selectAll() {
        this.setState({
            selections: this.props.organisers,
        });
    }
    deselectAll() {
        this.setState({
            selections: [],
        });
    }

    expandAll() {
        this.setState(prevState => {
            const newGroupsOpen = new Map(prevState.groupsOpen);
            for (let group of this.state.organisers) {
                newGroupsOpen.set(group.groupName, true);
            }
            return {
                groupsOpen: newGroupsOpen,
            };
        });
    }

    collapseAll() {
        this.setState(prevState => {
            const newGroupsOpen = new Map(prevState.groupsOpen);
            for (let group of this.state.organisers) {
                newGroupsOpen.set(group.groupName, false);
            }
            return {
                groupsOpen: newGroupsOpen,
            };
        });
    }

    async delete(file?: PublishedOrganiser) {
        const organisers = file ? [file] : this.state.selections;

        const result = await addAlert({
            type: AlertTypes.Confirm,
            text:
                "Are you sure you want to delete these organisers?<br />" +
                `<ul class="indented chevron"><li>${organisers
                    .map(item => item.name)
                    .join("</li><li>")}</li></ul>`,
            okText: "Yes",
            cancelText: "No",
        });

        if (result.response) {
            try {
                this.props.setPageLoading(true);
                await this.props.deleteOrganisers(organisers);
                this.setState({
                    selections: [],
                });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    async download(file?: PublishedOrganiser) {
        const organisers = file ? [file] : this.state.selections;

        const exportItems = organisers.map(
            item =>
                ({
                    fileId: item.dofid,
                    type: FileTypes.DOF, // Type for DOF
                    subType: QueuedExportSubTypes.Undefined, // Type for Undefined
                } as QueuedExportFile),
        );

        try {
            this.props.setPageLoading(true);
            await this.props.addExports(exportItems);

            addAlert({
                type: AlertTypes.Alert,
                text:
                    "Your export file is being created, this may take " +
                    "up to 10 minutes for large files.  We will email " +
                    `${this.props.user.username} when it's ready`,
            });
        } catch (error) {
            addAlert({
                type: AlertTypes.Alert,
                icon: "error",
                styleColor: "red",
                text:
                    "An error occured while trying to export these files, " +
                    "please try again later. If the problem persists, " +
                    "please contact us for support.",
            });
        } finally {
            this.props.setPageLoading(false);
        }
    }

    recycleBin() {
        this.props.openRecycled([RecycledType.PublishedOrganisers]);
    }

    render() {
        return (
            <>
                <section data-contextkey="organisers-menu">
                    <MainToolbarContents>
                        <ButtonMenu
                            icon="description"
                            text="Files"
                            tooltip="File management options">
                            <Button
                                icon="checklist_rtl"
                                text="Select All"
                                onClick={() => this.selectAll()}
                            />
                            <Button
                                icon="event_list"
                                text="Deselect All"
                                onClick={() => this.deselectAll()}
                            />
                            <Button
                                icon="add"
                                text="Expand All"
                                onClick={() => this.expandAll()}
                            />
                            <Button
                                icon="remove"
                                text="Collapse All"
                                onClick={() => this.collapseAll()}
                            />
                            <Button
                                icon="delete"
                                text="Delete Selected Items"
                                disabled={!this.state.selections.length}
                                onClick={() => this.delete()}
                            />
                            <Button
                                icon="file_download"
                                text="Download Selected Items"
                                disabled={!this.state.selections.length}
                                onClick={() => this.download()}
                            />
                        </ButtonMenu>
                        <ToolbarButton
                            icon="delete"
                            text="Recycle Bin"
                            onClick={() => this.recycleBin()}
                        />
                    </MainToolbarContents>

                    {this.props.organisers?.length > 0 ? (
                        <GeneralContainer
                            scaling={true}
                            mid={true}
                            max={true}
                            bufferSide={Side.All}
                            clearanceSide={Side.Top}
                            clearanceSize={Size.Large}
                            alignment={Alignment.Center}>
                            {this.state.organisers.map((group, index) => (
                                <Collapsible
                                    key={index}
                                    open={group.open}
                                    name={group.groupName}
                                    clearanceSide={Side.Bottom}
                                    onOpenChange={open =>
                                        this.toggleOpen(group.groupName, open)
                                    }>
                                    <List<PublishedOrganiser>
                                        joined={true}
                                        multiSelect={true}
                                        selections={this.state.selections}
                                        selectionChanged={(_, selections) =>
                                            this.setState({ selections })
                                        }>
                                        {group.organisers.map((file, index) => (
                                            <ListItem<PublishedOrganiser>
                                                key={index}
                                                data={file}
                                                panel={true}
                                                column={true}
                                                autoHeight={true}
                                                selectable={true}
                                                bufferSide={Side.All}
                                                contextkey="organisers-file"
                                                contextvalue={String(file.id)}>
                                                <div className="flex">
                                                    <div className="twothirds">
                                                        <h3 className="truncated">
                                                            {file.displayName}
                                                        </h3>
                                                    </div>

                                                    <div className="onethird text-right print-left">
                                                        {file.status ===
                                                        "Normal" ? (
                                                            <span>
                                                                <b>
                                                                    Published:
                                                                </b>
                                                                {file.publishedOn.toLocaleShort()}
                                                            </span>
                                                        ) : (
                                                            <span
                                                                className={`${
                                                                    file.status ===
                                                                    "Error"
                                                                        ? "error"
                                                                        : ""
                                                                }`}
                                                                dangerouslySetInnerHTML={{
                                                                    __html: file.statusMessage,
                                                                }}></span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="flex top-buffer-large">
                                                    <div className="inline">
                                                        <b>Start Date:</b>
                                                        {file.startDate.toLocale()}
                                                        &nbsp;
                                                    </div>
                                                    <div className="inline">
                                                        &nbsp;<b>End Date:</b>
                                                        {file.endDate.toLocale()}
                                                        &nbsp;
                                                    </div>
                                                    <div className="inline">
                                                        &nbsp;<b>Use:</b>{" "}
                                                        {file.useDays}
                                                        &nbsp;
                                                    </div>
                                                    {file.isMarkPublished && (
                                                        <div
                                                            className="markPublished text-right col"
                                                            data-tip="This organiser is published to integration endpoints">
                                                            <i className="blue">
                                                                language
                                                            </i>
                                                        </div>
                                                    )}
                                                </div>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapsible>
                            ))}
                        </GeneralContainer>
                    ) : (
                        <Text clearanceSide={Side.All}>No organisers found.</Text>
                    )}
                </section>

                <Recycled />

                <ContextFragment name="organisers-menu">
                    <ContextMenuItem
                        id="organisers_spacer_0"
                        separator={true}
                        contextKey="organisers-file"
                        order={0}
                    />
                    <ContextMenuItem
                        id="organisers_explorer_delete"
                        icon="delete"
                        name="Delete"
                        contextKey="organisers-file"
                        handler={(e, c) =>
                            this.delete(
                                this.props.organisers.find(
                                    item => String(item.id) === c.getValue(),
                                ),
                            )
                        }
                        order={4}
                    />
                    <ContextMenuItem
                        id="organisers_explorer_download"
                        icon="download"
                        name="Download"
                        contextKey="organisers-file"
                        handler={(e, c) =>
                            this.download(
                                this.props.organisers.find(
                                    item => String(item.id) === c.getValue(),
                                ),
                            )
                        }
                        order={5}
                    />
                    <ContextMenuItem
                        id="organisers_spacer_1"
                        separator={true}
                        contextKey="organisers-menu"
                        order={0}
                    />
                    <ContextMenuItem
                        id="organisers_expand_all"
                        icon="add"
                        name="Expand All"
                        contextKey="organisers-menu"
                        handler={this.expandAll.bind(this)}
                        order={1}
                    />
                    <ContextMenuItem
                        id="organisers_collapse_all"
                        icon="remove"
                        name="Collapse All"
                        contextKey="organisers-menu"
                        handler={this.collapseAll.bind(this)}
                        order={2}
                    />
                </ContextFragment>
            </>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { organisers } = state.db;
    const { session } = state.session;
    return {
        user: session.user,
        organisers: organisers.published,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
        initialisePublishedOrganiserDetails: () =>
            initialisePublishedOrganiserDetails(),
        deleteOrganisers: (organisers: PublishedOrganiser[]) =>
            unwrappedDispatch(deleteOrganisers(organisers)),
        addExports: (exports: QueuedExportFile[]) =>
            unwrappedDispatch(addExports(exports)),
        openRecycled: (types: RecycledType[]) => openRecycled(types),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Organisers);
